import { Collection } from '@nozbe/watermelondb';
import { ContactModel } from '../../../../types/Contacts';
import { HorseContactModel } from '../../../../types/HorseContact';
import { HorseModel, HorsePayload } from '../../../../types/Horses';
import { LocationModel } from '../../../../types/Location';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { LoggerFunction } from '../../../../types/logger';
import { getLocationIdToAssign, getUpdatedFieldValue } from '../../../utils';

export const prepareUpdateHorse = ({
    horseElement,
    payload,
    locationElement,
    fallbackLocationExists,
    logError,
    entityName,
}: {
    horseElement: HorseModel;
    payload: Partial<HorsePayload>;
    locationElement?: LocationModel;
    fallbackLocationExists: boolean;
    logError: LoggerFunction;
    entityName: string;
}) =>
    horseElement.prepareUpdate((horse) => {
        horse.name = payload.name || horseElement.name;
        horse.foalDate = getUpdatedFieldValue(
            payload.foalDate,
            horseElement.foalDate,
        );
        horse.gender = getUpdatedFieldValue(
            payload.gender,
            horseElement.gender,
        );
        horse.shoeSize = getUpdatedFieldValue(
            payload.shoeSize,
            horseElement.shoeSize,
        );
        horse.shoeType = getUpdatedFieldValue(
            payload.shoeType,
            horseElement.shoeType,
        );
        horse.shoeingCycle = getUpdatedFieldValue(
            payload.shoeingCycle,
            horseElement.shoeingCycle,
        );
        horse.breed = getUpdatedFieldValue(payload.breed, horseElement.breed);
        horse.workType = getUpdatedFieldValue(
            payload.workType,
            horseElement.workType,
        );
        horse.hidden =
            payload.hidden === undefined ? horseElement.hidden : payload.hidden;
        horse.comments = getUpdatedFieldValue(
            payload.comments,
            horseElement.comments,
        );
        horse.stableName = getUpdatedFieldValue(
            payload.stableName,
            horseElement.stableName,
        );
        horse.stableAddress = getUpdatedFieldValue(
            payload.stableAddress,
            horseElement.stableAddress,
        );
        horse.stablePhone = getUpdatedFieldValue(
            payload.stablePhone,
            horseElement.stablePhone,
        );
        horse.stablePhonePrefix = getUpdatedFieldValue(
            payload.stablePhonePrefix,
            horseElement.stablePhonePrefix,
        );
        horse.microchipNo = getUpdatedFieldValue(
            payload.microchipNo,
            horseElement.microchipNo,
        );
        horse.sireName = getUpdatedFieldValue(
            payload.sireName,
            horseElement.sireName,
        );
        horse.damName = getUpdatedFieldValue(
            payload.damName,
            horseElement.damName,
        );
        horse.locationId = getLocationIdToAssign({
            location: payload.location,
            locationElementId: locationElement?.id,
            address: payload.stableAddress,
            fallbackLocationId: horseElement.locationId,
            logError,
            entityName,
            fallbackLocationExists,
        });
        horse.userId = horseElement.userId;
        horse.organisationId = horseElement.organisationId;

        if (horseElement.hidden) {
            horse.shoeingCycleNotify = false;
            horse.shoeingCycleBadge = false;
        } else {
            horse.shoeingCycleNotify =
                payload.shoeingCycleNotify === undefined
                    ? horseElement.shoeingCycleNotify
                    : payload.shoeingCycleNotify;
            horse.shoeingCycleBadge =
                (payload.shoeingCycleNotify === false
                    ? false
                    : horseElement.shoeingCycleBadge) ||
                horseElement.shoeingCycleBadge;
        }
    });

export const prepareBumpHorse = ({
    horseElement,
}: {
    horseElement: HorseModel;
}) =>
    horseElement.prepareUpdate((horse) => {
        horse.name = horseElement.name;
    });

export const prepareUpdateHorseContacts = ({
    contacts,
    initialContacts,
    horseContacts,
    horseElement,
    horseContactsCollection,
}: {
    contacts?: HorsePayload['contacts'];
    initialContacts: ContactModel[];
    horseContacts: HorseContactModel[];
    horseElement: HorseModel;
    horseContactsCollection: Collection<HorseContactModel>;
}) => {
    const horseContactsToAdd: HorseContactModel[] = [];
    const horseContactsToDelete: HorseContactModel[] = [];

    if (!contacts) {
        return { horseContactsToAdd, horseContactsToDelete };
    }

    contacts.forEach((contact) => {
        if (!initialContacts.find(({ id }) => id === contact.id)) {
            horseContactsToAdd.push(
                horseContactsCollection.prepareCreate((horseContact) => {
                    horseContact.horseId = horseElement.id;
                    horseContact.userId = horseElement.userId;
                    horseContact.organisationId = horseElement.organisationId;
                    horseContact.contactId = contact.id;
                }),
            );
        }
    });

    horseContacts.forEach((horseContact) => {
        if (!contacts.find(({ id }) => id === horseContact.contactId)) {
            horseContactsToDelete.push(horseContact.prepareMarkAsDeleted());
        }
    });

    return { horseContactsToAdd, horseContactsToDelete };
};

export const logUpdateHorseActions = ({
    horse,
    horseContactsToAdd,
    horseContactsToDelete,
    logDBAction,
    tableName,
}: {
    horse: HorseModel;
    horseContactsToAdd: HorseContactModel[];
    horseContactsToDelete: HorseContactModel[];
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    logDBAction({
        message: 'Update horse',
        modelName: tableName,
        payload: horse,
    });
    logDBAction({
        message: 'Update horse - create horse contacts',
        modelName: tableName,
        payload: horseContactsToAdd,
    });
    logDBAction({
        message: 'Update horse - delete horse contacts',
        modelName: tableName,
        payload: horseContactsToDelete,
    });
};
